import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaArrowRight,
  FaArrowLeft,
  FaSpinner,
} from 'react-icons/fa';
import {
  LuArrowLeftRight,
  LuBuilding2,
  LuCalendarDays,
  LuFileText,
  LuListChecks,
  LuMapPin,
  LuMapPinned,
  LuPackage,
  LuPlus,
  LuTrash2,
} from 'react-icons/lu';
import WebsiteTopBar from '../../components/WebsiteTopBar';
import HubHomeFooter from '../../components/hub/HubHomeFooter';
import { useLocationPicker } from '../../hooks/useLocationPicker';
import { useCurrencyMaster } from '../../hooks/useCurrencyMaster';
import { LOGISTICS_TRANSPORT_MODES } from '../../constants/logisticsProfileFormOptions';
import {
  checkBusinessRelocationDuplicate,
  submitBusinessRelocationRfq,
} from '../../services/businessRelocationRfq.service';
import SuperAdminStorageService from '../../services/SuperAdminStorageService';
import {
  BR_ADDRESS_MAX_WORDS,
  BR_BUDGET_RANGE_OPTIONS,
  BR_MAX_SUPPLEMENTAL_LOCATIONS,
  BR_SPECIAL_NOTES_MAX_WORDS,
  BR_URGENCY_OPTIONS,
  BR_WEIGHT_UNIT_OPTIONS,
  getBusinessRelocationRfqValidationError,
  buildSupplementalLocationsPayload,
  countWords,
  createBrLocationEntry,
  generateBrRfqReference,
  getInitialBusinessRelocationRfqForm,
  includesOfficeRelocationScope,
  isBrLocationEntryComplete,
  isBrLocationEntryEmpty,
} from './businessRelocationRfqFormState';
import { VALUE_CURRENCY_OPTIONS } from './logisticsGenericRfqFormState';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/website-top-bar.scss';
import '../../assets/styles/business_relocation_rfq.scss';
import {
  fetchBuyerRefCompanyFieldsForRfqPrefill,
  fetchBuyerCurrentLocationPrefillForBusinessRelocation,
  mergeBuyerPrefillIntoForm,
  mergeBusinessRelocationLocationPrefill,
} from './logisticsRfqBuyerPrefill';
import { LOGISTICS_RFQ_ROUTES } from '../../constants/logisticsRfqRoutes';
import { getLogisticsRfqPostSubmitPath } from '../../utils/logisticsRfqPostSubmitNavigate';

/** e.g. `9-` or `9` from query → `9` */
function parseCompanyIdFromSearchParam(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const m = String(raw).trim().match(/^(\d+)/);
  const n = m ? parseInt(m[1], 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function createClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `br-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function formatDuplicateStatus(status) {
  return String(status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDuplicateSubmittedDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getReviewExistingRfqPath(existingRfq) {
  if (SuperAdminStorageService.isAuthenticated()) {
    const id = existingRfq?.id;
    if (id != null && String(id).trim() !== '') {
      return `/super-admin/logistics/business-relocation-rfqs?viewId=${encodeURIComponent(String(id))}`;
    }
    return '/super-admin/logistics/business-relocation-rfqs';
  }
  const id = existingRfq?.id;
  if (id != null && String(id).trim() !== '') {
    return `/logistics/my-rfqs?businessRelocationRfqId=${encodeURIComponent(String(id))}`;
  }
  return '/logistics/my-rfqs';
}

function Field({ label, required, children }) {
  return (
    <div className="lbrq-field">
      <label className="lbrq-label">
        {label}
        {required ? <span className="lbrq-required">*</span> : null}
      </label>
      {children}
    </div>
  );
}

function TextareaField({
  label,
  required,
  value,
  onChange,
  placeholder,
  rows = 4,
  maxWords = BR_ADDRESS_MAX_WORDS,
}) {
  const words = countWords(value);
  const atLimit = words >= maxWords;

  const handleChange = (e) => {
    const next = e.target.value;
    if (countWords(next) <= maxWords) {
      onChange(e);
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData?.getData('text') ?? '';
    const el = e.target;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const current = typeof value === 'string' ? value : '';
    const next = current.slice(0, start) + paste + current.slice(end);
    if (countWords(next) > maxWords) {
      e.preventDefault();
    }
  };

  return (
    <Field label={label} required={required}>
      <div className="lbrq-textarea-wrap">
        <textarea
          className={`lbrq-textarea${atLimit ? ' lbrq-textarea--at-limit' : ''}`}
          rows={rows}
          value={value}
          onChange={handleChange}
          onPaste={handlePaste}
          placeholder={placeholder}
        />
        <span
          className={`lbrq-char-count${atLimit ? ' lbrq-char-count--limit' : ''}`}
          aria-live="polite"
        >
          {words}/{maxWords} words
        </span>
      </div>
    </Field>
  );
}

function Section({ icon, title, subtitle, tone, children }) {
  return (
    <section className={`lbrq-block lbrq-block--${tone}`}>
      <header className="lbrq-block-head">
        <span className="lbrq-block-icon" aria-hidden>
          {icon}
        </span>
        <div>
          <h2 className="lbrq-block-title">{title}</h2>
          {subtitle ? <p className="lbrq-block-sub">{subtitle}</p> : null}
        </div>
      </header>
      <div className="lbrq-block-body">{children}</div>
    </section>
  );
}

/** Country → state → city from `/master/company-*` (same pattern as LogisticsGenericRfqPage). */
function BrLocationFields({
  labels,
  fieldKeys,
  loc,
  form,
  setForm,
  addressPlaceholder,
  pincodePlaceholder,
}) {
  const ck = fieldKeys.country;
  const sk = fieldKeys.state;
  const cyk = fieldKeys.city;
  const pk = fieldKeys.pincode;
  const ak = fieldKeys.address;

  return (
    <>
      <div className="lbrq-row lbrq-row--4">
        <Field label={labels.country} required>
          <div className="lbrq-select-wrap">
            <select
              className="lbrq-select"
              value={form[ck] != null && form[ck] !== '' ? String(form[ck]) : ''}
              disabled={loc.loading}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => ({ ...prev, [ck]: v, [sk]: '', [cyk]: '' }));
                void loc.loadStates(v);
              }}
            >
              <option value="">{loc.loading ? 'Loading…' : 'Select country'}</option>
              {loc.countries.map((c) => (
                <option key={String(c.id)} value={String(c.id)}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={labels.state} required>
          <div className="lbrq-select-wrap">
            <select
              className="lbrq-select"
              value={form[sk] != null && form[sk] !== '' ? String(form[sk]) : ''}
              disabled={!form[ck] || loc.loadingStates}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => ({ ...prev, [sk]: v, [cyk]: '' }));
                void loc.loadCities(v);
              }}
            >
              <option value="">{loc.loadingStates ? 'Loading…' : 'Select state'}</option>
              {loc.states.map((s) => (
                <option key={String(s.id)} value={String(s.id)}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={labels.city} required>
          <div className="lbrq-select-wrap">
            <select
              className="lbrq-select"
              value={form[cyk] != null && form[cyk] !== '' ? String(form[cyk]) : ''}
              disabled={!form[sk] || loc.loadingCities}
              onChange={(e) => setForm((prev) => ({ ...prev, [cyk]: e.target.value }))}
            >
              <option value="">{loc.loadingCities ? 'Loading…' : 'Select city'}</option>
              {loc.cities.map((c) => (
                <option key={String(c.id)} value={String(c.id)}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={labels.pincode} required>
          <input
            className="lbrq-input"
            value={form[pk]}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                [pk]: e.target.value.replace(/[^A-Za-z0-9\s-]/g, '').slice(0, 12),
              }))
            }
            placeholder={pincodePlaceholder || 'e.g., 400001, 90210'}
          />
        </Field>
      </div>
      <TextareaField
        label={labels.address}
        required
        value={form[ak]}
        onChange={(e) => setForm((prev) => ({ ...prev, [ak]: e.target.value }))}
        placeholder={
          addressPlaceholder || 'Complete address with floor, building name, landmarks'
        }
      />
    </>
  );
}

function BrExtraLocationCard({ index, entry, onChange, onRemove, labels }) {
  const loc = useLocationPicker();

  useEffect(() => {
    if (entry.country) {
      void loc.loadStates(String(entry.country));
    } else {
      void loc.loadStates('');
    }
  }, [entry.country, loc.loadStates]);

  useEffect(() => {
    if (entry.state) {
      void loc.loadCities(String(entry.state));
    } else {
      void loc.loadCities('');
    }
  }, [entry.state, loc.loadCities]);

  return (
    <div className="lbrq-extra-loc">
      <div className="lbrq-extra-loc__head">
        <span className="lbrq-extra-loc__title">Additional address {index + 1}</span>
        <button type="button" className="lbrq-extra-loc__remove" onClick={onRemove}>
          <LuTrash2 aria-hidden />
          Remove
        </button>
      </div>
      <div className="lbrq-row lbrq-row--4">
        <Field label={labels.country} required>
          <div className="lbrq-select-wrap">
            <select
              className="lbrq-select"
              value={entry.country != null && entry.country !== '' ? String(entry.country) : ''}
              disabled={loc.loading}
              onChange={(e) => onChange({ country: e.target.value, state: '', city: '' })}
            >
              <option value="">{loc.loading ? 'Loading…' : 'Select country'}</option>
              {loc.countries.map((c) => (
                <option key={String(c.id)} value={String(c.id)}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={labels.state} required>
          <div className="lbrq-select-wrap">
            <select
              className="lbrq-select"
              value={entry.state != null && entry.state !== '' ? String(entry.state) : ''}
              disabled={!entry.country || loc.loading}
              onChange={(e) => onChange({ state: e.target.value, city: '' })}
            >
              <option value="">Select state</option>
              {loc.states.map((s) => (
                <option key={String(s.id)} value={String(s.id)}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={labels.city} required>
          <div className="lbrq-select-wrap">
            <select
              className="lbrq-select"
              value={entry.city != null && entry.city !== '' ? String(entry.city) : ''}
              disabled={!entry.state || loc.loading}
              onChange={(e) => onChange({ city: e.target.value })}
            >
              <option value="">Select city</option>
              {loc.cities.map((c) => (
                <option key={String(c.id)} value={String(c.id)}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={labels.pincode} required>
          <input
            className="lbrq-input"
            value={entry.pincode || ''}
            onChange={(e) =>
              onChange({
                pincode: e.target.value.replace(/[^A-Za-z0-9\s-]/g, '').slice(0, 12),
              })
            }
            placeholder="Pincode / ZIP"
          />
        </Field>
      </div>
      <TextareaField
        label={labels.address}
        required
        value={entry.address || ''}
        onChange={(e) => onChange({ address: e.target.value })}
        placeholder="Complete address with floor, building, landmarks"
      />
    </div>
  );
}

function BrExtraLocationsList({ listKey, locations, setForm, labels, addLabel }) {
  const items = Array.isArray(locations) ? locations : [];

  const addLocation = () => {
    if (items.length >= BR_MAX_SUPPLEMENTAL_LOCATIONS) {
      toast.info(`You can add up to ${BR_MAX_SUPPLEMENTAL_LOCATIONS} additional addresses.`);
      return;
    }
    setForm((prev) => ({
      ...prev,
      [listKey]: [...(prev[listKey] || []), createBrLocationEntry()],
    }));
  };

  return (
    <div className="lbrq-extra-locs">
      {items.map((entry, idx) => (
        <BrExtraLocationCard
          key={entry.id}
          index={idx}
          entry={entry}
          labels={labels}
          onRemove={() =>
            setForm((prev) => ({
              ...prev,
              [listKey]: (prev[listKey] || []).filter((e) => e.id !== entry.id),
            }))
          }
          onChange={(patch) =>
            setForm((prev) => ({
              ...prev,
              [listKey]: (prev[listKey] || []).map((e) =>
                e.id === entry.id ? { ...e, ...patch } : e,
              ),
            }))
          }
        />
      ))}
      <button type="button" className="lbrq-add-address-btn" onClick={addLocation}>
        <LuPlus aria-hidden />
        {addLabel}
      </button>
    </div>
  );
}

export default function BusinessRelocationRfqPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyIdParam = useMemo(
    () => parseCompanyIdFromSearchParam(searchParams.get('companyId')),
    [searchParams],
  );

  const [form, setForm] = useState(() => getInitialBusinessRelocationRfqForm(companyIdParam));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(null);
  const submitInFlightRef = useRef(false);
  const clientRequestIdRef = useRef(null);

  useEffect(() => {
    setForm((prev) => ({ ...prev, companyId: companyIdParam }));
  }, [companyIdParam]);

  useEffect(() => {
    setForm((prev) => {
      if (prev.rfqReferenceNumber) return prev;
      return { ...prev, rfqReferenceNumber: generateBrRfqReference() };
    });
  }, []);

  /** Buyer contact/company + registered address → current location (fills blanks only). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mapped = await fetchBuyerRefCompanyFieldsForRfqPrefill();
      if (!cancelled) {
        setForm((prev) => mergeBuyerPrefillIntoForm(prev, mapped, 'business-relocation'));
      }
      const locationPatch = await fetchBuyerCurrentLocationPrefillForBusinessRelocation();
      if (!cancelled && locationPatch) {
        setForm((prev) => mergeBusinessRelocationLocationPrefill(prev, locationPatch));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyIdParam]);

  const currentLoc = useLocationPicker();
  const destLoc = useLocationPicker();
  const { currencyOptions } = useCurrencyMaster();

  const totalValueCurrencyOptions = useMemo(() => {
    if (Array.isArray(currencyOptions) && currencyOptions.length > 0) {
      const seen = new Set();
      return currencyOptions
        .map((opt) => {
          const raw =
            (opt.currencyCode != null && String(opt.currencyCode).trim()) ||
            (typeof opt.value === 'string' && String(opt.value).trim()) ||
            '';
          const code = /^[A-Za-z]{2,4}$/.test(raw) ? raw.toUpperCase() : raw;
          return { value: code, label: opt.label || code };
        })
        .filter((o) => o.value && !seen.has(o.value) && seen.add(o.value));
    }
    return VALUE_CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }));
  }, [currencyOptions]);

  useEffect(() => {
    if (form.currentCountry) {
      void currentLoc.loadStates(String(form.currentCountry));
    } else {
      void currentLoc.loadStates('');
    }
  }, [form.currentCountry, currentLoc.loadStates]);

  useEffect(() => {
    if (form.currentState) {
      void currentLoc.loadCities(String(form.currentState));
    } else {
      void currentLoc.loadCities('');
    }
  }, [form.currentState, currentLoc.loadCities]);

  useEffect(() => {
    if (form.destCountry) {
      void destLoc.loadStates(String(form.destCountry));
    } else {
      void destLoc.loadStates('');
    }
  }, [form.destCountry, destLoc.loadStates]);

  useEffect(() => {
    if (form.destState) {
      void destLoc.loadCities(String(form.destState));
    } else {
      void destLoc.loadCities('');
    }
  }, [form.destState, destLoc.loadCities]);

  const setVal = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleService = useCallback((key) => {
    setForm((prev) => ({
      ...prev,
      additionalServices: {
        ...prev.additionalServices,
        [key]: !prev.additionalServices[key],
      },
    }));
  }, []);

  const showOfficeRelocationTools = includesOfficeRelocationScope(form.relocationScope);
  const showLocationSections = Boolean(form.relocationScope);

  const validateForm = useCallback(() => {
    const error = getBusinessRelocationRfqValidationError(form);
    if (error) {
      toast.error(error);
      return false;
    }
    return true;
  }, [form]);

  const performSubmit = useCallback(
    async ({ confirmDuplicateSubmit = false } = {}) => {
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;
      setIsSubmitting(true);

      if (confirmDuplicateSubmit) {
        clientRequestIdRef.current = createClientRequestId();
      } else if (!clientRequestIdRef.current) {
        clientRequestIdRef.current = createClientRequestId();
      }

      const payload = {
        ...form,
        supplementalLocations: buildSupplementalLocationsPayload(form),
        companyId: companyIdParam ?? form.companyId ?? null,
        ...(confirmDuplicateSubmit
          ? { rfqReferenceNumber: generateBrRfqReference() }
          : {}),
      };

      try {
        const res = await submitBusinessRelocationRfq(payload, {
          confirmDuplicateSubmit,
          clientRequestId: clientRequestIdRef.current,
        });

        if (res?.success) {
          toast.success(
            res?.message || 'Business Relocation RFQ submitted successfully.',
          );
          clientRequestIdRef.current = null;
          setDuplicateModal(null);
          navigate(getLogisticsRfqPostSubmitPath('business-relocation'), { replace: true });
        } else {
          toast.error(res?.message || 'Submission failed.');
        }
      } catch (err) {
        if (err?.response?.status === 409) {
          const m = err?.response?.data?.message || 'You have already submitted this RFQ.';
          toast.error(typeof m === 'string' ? m : 'You have already submitted this RFQ.');
          return;
        }
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Could not submit. Please try again.';
        toast.error(typeof msg === 'string' ? msg : 'Submission failed.');
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [form, companyIdParam, navigate],
  );

  const submit = useCallback(async () => {
    if (!validateForm()) return;
    if (submitInFlightRef.current) return;

    setIsSubmitting(true);
    try {
      const dupForm = {
        relocationScope: form.relocationScope,
        currentAddress: form.currentAddress,
        destAddress: form.destAddress,
        preferredMoveDate: form.preferredMoveDate,
      };
      const dupRes = await checkBusinessRelocationDuplicate(dupForm);
      const dupData = dupRes?.data ?? dupRes;
      if (dupData?.hasDuplicate && dupData?.existingRfq) {
        setDuplicateModal(dupData.existingRfq);
        setIsSubmitting(false);
        return;
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Could not check for similar RFQs. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not continue.');
      setIsSubmitting(false);
      return;
    }

    await performSubmit({ confirmDuplicateSubmit: false });
  }, [form, validateForm, performSubmit]);

  const handleSubmitAnyway = useCallback(() => {
    void performSubmit({ confirmDuplicateSubmit: true });
  }, [performSubmit]);

  const handleReviewExistingRfq = useCallback(() => {
    if (!duplicateModal) return;
    const path = getReviewExistingRfqPath(duplicateModal);
    setDuplicateModal(null);
    submitInFlightRef.current = false;
    setIsSubmitting(false);
    navigate(path);
  }, [duplicateModal, navigate]);

  const closeDuplicateModal = useCallback(() => {
    setDuplicateModal(null);
    submitInFlightRef.current = false;
    setIsSubmitting(false);
  }, []);

  const providerLabel =
    companyIdParam != null ? `Provider company ID: ${companyIdParam}` : 'No specific provider selected';

  return (
    <div className="lbrq-page">
      <WebsiteTopBar />
      <main className="lbrq-main">
        <div className="lbrq-breadcrumb-row">
          <nav className="lbrq-breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <span className="lbrq-breadcrumb-sep">&gt;</span>
            <Link to={LOGISTICS_RFQ_ROUTES.selection}>RFQ</Link>
            <span className="lbrq-breadcrumb-sep">&gt;</span>
            <span className="lbrq-breadcrumb-current">Logistics</span>
          </nav>
          <Link to={LOGISTICS_RFQ_ROUTES.selection} className="lbrq-back-link">
            ← Back
          </Link>
        </div>

        <div className="lbrq-top">
          <div className="lbrq-title-row">
            <span className="lbrq-title-icon" aria-hidden>
              <LuFileText />
            </span>
            <div className="lbrq-title-text">
              <h1 className="lbrq-page-title">Business Relocation Quote</h1>
              <p className="lbrq-page-subtitle">
                Provide your relocation details and get the best quotes from verified logistics partners.
              </p>
            </div>
          </div>
          {companyIdParam != null ? (
            <p className="lbrq-provider-pill">{providerLabel}</p>
          ) : null}
        </div>

        <div className="lbrq-card">
          <div className="lbrq-card-inner">
            <Section icon={<LuBuilding2 />} title="Contact & Company Information" tone="orange">
              <div className="lbrq-row lbrq-row--4">
                <Field label="Contact Person" required>
                  <input
                    className="lbrq-input"
                    value={form.contactPerson}
                    onChange={(e) => setVal('contactPerson', e.target.value)}
                    placeholder="HR Manager / Operations Head"
                  />
                </Field>
                <Field label="Contact Number" required>
                  <input
                    type="tel"
                    className="lbrq-input"
                    value={form.contactNumber}
                    onChange={(e) =>
                      setVal('contactNumber', e.target.value.replace(/[^\d+\s()-]/g, ''))
                    }
                    placeholder="+91 88765 43210"
                  />
                </Field>
                <Field label="Company Name" required>
                  <input
                    className="lbrq-input"
                    value={form.companyName}
                    onChange={(e) => setVal('companyName', e.target.value)}
                    placeholder="Your company name"
                  />
                </Field>
                <Field label="Company Email" required>
                  <input
                    type="email"
                    className="lbrq-input"
                    value={form.companyEmail}
                    onChange={(e) => setVal('companyEmail', e.target.value)}
                    placeholder="company@example.com"
                  />
                </Field>
              </div>
            </Section>

            <Section icon={<LuArrowLeftRight />} title="Type of Relocation" tone="orange">
              <div className="lbrq-relocation-type" role="group" aria-label="Type of relocation">
                {[
                  { value: 'office', label: 'Office Relocation' },
                  { value: 'employee', label: 'Employee Relocation' },
                  { value: 'both', label: 'Both Office & Employee' },
                ].map((opt) => (
                  <label key={opt.value} className="lbrq-relocation-pill">
                    <input
                      type="checkbox"
                      checked={form.relocationScope === opt.value}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const nextScope = opt.value;
                          setForm((prev) => ({
                            ...prev,
                            relocationScope: nextScope,
                            ...(includesOfficeRelocationScope(nextScope)
                              ? {}
                              : {
                                  additionalCurrentLocations: [],
                                  additionalDestLocations: [],
                                }),
                          }));
                        } else {
                          setVal('relocationScope', '');
                        }
                      }}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </Section>

            {!showLocationSections ? (
              <p className="lbrq-location-hint">
                Select a relocation type above to enter current and destination location details.
              </p>
            ) : (
              <div className="lbrq-location-pair">
                  <Section
                    icon={<LuMapPin />}
                    title="Current Location Details"
                    subtitle={
                      showOfficeRelocationTools
                        ? 'Primary office or facility you are moving from. Add more addresses if you have multiple pickup sites.'
                        : form.relocationScope === 'employee'
                          ? 'Employee’s current residence or pickup location.'
                          : undefined
                    }
                    tone="orange"
                  >
                    <BrLocationFields
                      labels={{
                        country: 'Current Country',
                        state: 'Current State/Province',
                        city: 'Current City',
                        pincode: 'Current Pincode/ZIP',
                        address: 'Complete Current Address',
                      }}
                      fieldKeys={{
                        country: 'currentCountry',
                        state: 'currentState',
                        city: 'currentCity',
                        pincode: 'currentPincode',
                        address: 'currentAddress',
                      }}
                      loc={currentLoc}
                      form={form}
                      setForm={setForm}
                    />
                    {showOfficeRelocationTools ? (
                      <BrExtraLocationsList
                        listKey="additionalCurrentLocations"
                        locations={form.additionalCurrentLocations}
                        setForm={setForm}
                        addLabel="Add another current address"
                        labels={{
                          country: 'Country',
                          state: 'State / Province',
                          city: 'City',
                          pincode: 'Pincode / ZIP',
                          address: 'Complete Address',
                        }}
                      />
                    ) : null}
                  </Section>

                  <Section
                    icon={<LuMapPinned />}
                    title="Destination Location Details"
                    subtitle={
                      showOfficeRelocationTools
                        ? 'Primary destination office or facility. Add more addresses if goods go to multiple sites.'
                        : form.relocationScope === 'employee'
                          ? 'Employee’s destination residence or delivery location.'
                          : undefined
                    }
                    tone="orange"
                  >
                    <BrLocationFields
                      labels={{
                        country: 'Destination Country',
                        state: 'Destination State/Province',
                        city: 'Destination City',
                        pincode: 'Destination Pincode/ZIP',
                        address: 'Complete Destination Address',
                      }}
                      fieldKeys={{
                        country: 'destCountry',
                        state: 'destState',
                        city: 'destCity',
                        pincode: 'destPincode',
                        address: 'destAddress',
                      }}
                      loc={destLoc}
                      form={form}
                      setForm={setForm}
                      pincodePlaceholder="e.g., 560001, 73301"
                      addressPlaceholder="Complete destination address with floor, building name, landmarks"
                    />
                    {showOfficeRelocationTools ? (
                      <BrExtraLocationsList
                        listKey="additionalDestLocations"
                        locations={form.additionalDestLocations}
                        setForm={setForm}
                        addLabel="Add another destination address"
                        labels={{
                          country: 'Country',
                          state: 'State / Province',
                          city: 'City',
                          pincode: 'Pincode / ZIP',
                          address: 'Complete Address',
                        }}
                      />
                    ) : null}
                  </Section>
                </div>
            )}

            <Section icon={<LuPackage />} title="Total Shipment Summary" tone="orange">
              <div className="lbrq-row lbrq-row--3">
                <Field label="Total Estimated Weight" required>
                  <div className="lbrq-inline-value-unit">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="lbrq-input lbrq-input--grow"
                      value={form.totalWeightKg}
                      onChange={(e) => {
                        const next = e.target.value.replace(/[^\d.]/g, '');
                        setVal('totalWeightKg', next);
                      }}
                      placeholder="e.g., 5000"
                    />
                    <div className="lbrq-select-wrap lbrq-select-wrap--narrow">
                      <select
                        className="lbrq-select"
                        value={form.totalWeightUnit || 'KG'}
                        onChange={(e) => setVal('totalWeightUnit', e.target.value)}
                      >
                        {BR_WEIGHT_UNIT_OPTIONS.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </Field>
                <Field label="Total Number of Packages" required>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="lbrq-input"
                    value={form.totalPackages}
                    onChange={(e) => setVal('totalPackages', e.target.value.replace(/\D/g, ''))}
                    placeholder="e.g., 350"
                  />
                </Field>
                <Field label="Total Estimated Value" required>
                  <div className="lbrq-inline-value-unit">
                    <div className="lbrq-select-wrap lbrq-select-wrap--currency">
                      <select
                        className="lbrq-select"
                        value={form.totalValueCurrency || 'USD'}
                        onChange={(e) => setVal('totalValueCurrency', e.target.value)}
                        aria-label="Total estimated value currency"
                      >
                        {totalValueCurrencyOptions.map((currency) => (
                          <option key={currency.value} value={currency.value}>
                            {currency.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="lbrq-input lbrq-input--grow"
                      value={form.totalValueUsd}
                      onChange={(e) => {
                        const next = e.target.value.replace(/[^\d.]/g, '');
                        setVal('totalValueUsd', next);
                      }}
                      placeholder="e.g., 100000"
                    />
                  </div>
                </Field>
              </div>
            </Section>

            <Section icon={<LuCalendarDays />} title="Timeline & Preferences" tone="orange">
              <div className="lbrq-row lbrq-row--4">
                <Field label="Preferred Move Date" required>
                  <div className="lbrq-input-wrap lbrq-input-wrap--date">
                    <input
                      type="date"
                      className="lbrq-input lbrq-input--date"
                      value={form.preferredMoveDate}
                      onChange={(e) => setVal('preferredMoveDate', e.target.value)}
                    />
                    <span className="lbrq-input-suffix-icon" aria-hidden>
                      <LuCalendarDays />
                    </span>
                  </div>
                </Field>
                <Field label="Urgency Level" required>
                  <div className="lbrq-select-wrap">
                    <select
                      className="lbrq-select"
                      value={form.urgencyLevel}
                      onChange={(e) => setVal('urgencyLevel', e.target.value)}
                    >
                      {BR_URGENCY_OPTIONS.map((t) => (
                        <option key={t || 'x'} value={t}>
                          {t || 'Select urgency'}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
                <Field label="Preferred Logistics Mode" required>
                  <div className="lbrq-select-wrap">
                    <select
                      className="lbrq-select"
                      value={form.logisticsMode}
                      onChange={(e) => setVal('logisticsMode', e.target.value)}
                    >
                      <option value="">Select mode</option>
                      {LOGISTICS_TRANSPORT_MODES.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
                <Field label="Budget Range (USD)" required>
                  <div className="lbrq-select-wrap">
                    <select
                      className="lbrq-select"
                      value={form.budgetRange}
                      onChange={(e) => setVal('budgetRange', e.target.value)}
                    >
                      {BR_BUDGET_RANGE_OPTIONS.map((t) => (
                        <option key={t || 'x'} value={t}>
                          {t || 'Select budget range'}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
              </div>
              <TextareaField
                label="Special Requirements & Notes"
                value={form.specialNotes}
                onChange={(e) => setVal('specialNotes', e.target.value)}
                placeholder="Any special handling requirements, access restrictions, timing constraints..."
                maxWords={BR_SPECIAL_NOTES_MAX_WORDS}
              />
            </Section>

            <Section icon={<LuListChecks />} title="Additional Services Required" tone="orange">
              <div className="lbrq-svc-grid">
                <label className="lbrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.professionalPacking}
                    onChange={() => toggleService('professionalPacking')}
                  />
                  Professional Packing Service
                </label>
                <label className="lbrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.unpackingSetup}
                    onChange={() => toggleService('unpackingSetup')}
                  />
                  Unpacking &amp; Setup Service
                </label>
                <label className="lbrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.tempStorage}
                    onChange={() => toggleService('tempStorage')}
                  />
                  Temporary Storage Required
                </label>
                <label className="lbrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.insuranceCoverage}
                    onChange={() => toggleService('insuranceCoverage')}
                  />
                  Insurance Coverage
                </label>
                <label className="lbrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.customsClearance}
                    onChange={() => toggleService('customsClearance')}
                  />
                  Customs Clearance (International)
                </label>
                <label className="lbrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.vehicleTransport}
                    onChange={() => toggleService('vehicleTransport')}
                  />
                  Vehicle Transportation
                </label>
                <label className="lbrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.petRelocation}
                    onChange={() => toggleService('petRelocation')}
                  />
                  Pet Relocation Service
                </label>
                <label className="lbrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.realTimeTracking}
                    onChange={() => toggleService('realTimeTracking')}
                  />
                  Real-time Tracking
                </label>
              </div>
            </Section>

            <div className="lbrq-illustration" aria-hidden>
              <svg className="lbrq-illustration-svg" viewBox="0 0 960 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="95" width="960" height="45" fill="#e8ecf4" />
                <rect x="40" y="72" width="28" height="68" rx="2" fill="#c5d0e0" />
                <rect x="80" y="58" width="36" height="82" rx="2" fill="#b8c5d8" />
                <rect x="130" y="48" width="44" height="92" rx="2" fill="#a8b8cf" />
                <rect x="190" y="62" width="32" height="78" rx="2" fill="#c5d0e0" />
                <rect x="240" y="40" width="52" height="100" rx="2" fill="#9aaec8" />
                <rect x="310" y="55" width="38" height="85" rx="2" fill="#b8c5d8" />
                <rect x="520" y="50" width="48" height="90" rx="2" fill="#a8b8cf" />
                <rect x="580" y="65" width="34" height="75" rx="2" fill="#c5d0e0" />
                <rect x="640" y="44" width="56" height="96" rx="2" fill="#9aaec8" />
                <rect x="720" y="58" width="40" height="82" rx="2" fill="#b8c5d8" />
                <rect x="780" y="70" width="30" height="70" rx="2" fill="#c5d0e0" />
                <rect x="830" y="52" width="46" height="88" rx="2" fill="#a8b8cf" />
                <rect x="380" y="78" width="120" height="42" rx="6" fill="#ff6a00" />
                <rect x="395" y="68" width="90" height="28" rx="4" fill="#ff8533" />
                <circle cx="410" cy="122" r="14" fill="#475569" />
                <circle cx="410" cy="122" r="7" fill="#94a3b8" />
                <circle cx="470" cy="122" r="14" fill="#475569" />
                <circle cx="470" cy="122" r="7" fill="#94a3b8" />
              </svg>
            </div>
          </div>

          <footer className="lbrq-card-footer">
            <Link
              to="/home"
              className="lbrq-btn lbrq-btn--outline"
              aria-disabled={isSubmitting}
              onClick={(e) => {
                if (isSubmitting) e.preventDefault();
              }}
            >
              <FaArrowLeft className="lbrq-btn-back-icon" aria-hidden />
              Back
            </Link>
            <button
              type="button"
              className="lbrq-btn lbrq-btn--primary"
              onClick={submit}
              disabled={isSubmitting || Boolean(duplicateModal)}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="lbrq-btn-spinner" aria-hidden />
                  Submitting…
                </>
              ) : (
                <>
                  Get Business Relocation Quote
                  <FaArrowRight className="lbrq-btn-arrow" aria-hidden />
                </>
              )}
            </button>
          </footer>
        </div>
      </main>
      <HubHomeFooter />
      {duplicateModal && typeof document !== 'undefined'
        ? createPortal(
            <div className="lbrq-dup-overlay" role="presentation" onClick={closeDuplicateModal}>
              <div
                className="lbrq-dup-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="lbrq-dup-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="lbrq-dup-title" className="lbrq-dup-title">
                  Similar RFQ found
                </h2>
                <p className="lbrq-dup-lead">
                  You submitted a very similar business relocation request in the last 24 hours.
                  You can review it or submit a new RFQ with a new reference number.
                </p>
                <dl className="lbrq-dup-meta">
                  <div>
                    <dt>RFQ Number</dt>
                    <dd>{duplicateModal.rfqNumber || duplicateModal.rfqReference || '—'}</dd>
                  </div>
                  <div>
                    <dt>Submitted</dt>
                    <dd>{formatDuplicateSubmittedDate(duplicateModal.submittedDate)}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{formatDuplicateStatus(duplicateModal.status)}</dd>
                  </div>
                </dl>
                <div className="lbrq-dup-actions">
                  <button
                    type="button"
                    className="lbrq-btn lbrq-btn--outline"
                    onClick={handleReviewExistingRfq}
                    disabled={isSubmitting}
                  >
                    Review Existing RFQ
                  </button>
                  <button
                    type="button"
                    className="lbrq-btn lbrq-btn--primary"
                    onClick={handleSubmitAnyway}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting…' : 'Submit Anyway'}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
